import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, type RefObject } from 'react'

import { ROW_HEIGHT } from '@/components/data-table/virtual-blocks'

/** İlk çizimde kapsayıcı henüz ölçülmediğinde varsayılan görünür yükseklik (px). */
const INITIAL_HEIGHT = 600

interface UseVirtualRowsOptions {
  /** Toplam satır sayısı (yüklenmemişler dahil); 0 = sanallaştırma kapalı. */
  count: number
  scrollRef: RefObject<HTMLElement | null>
  /** Kaydırma kapsayıcısının başındaki sabit başlık (+ filtre satırı) yüksekliği (px). */
  headHeight: number
  /** Sonundaki sabit toplam satırlarının yüksekliği (px). */
  footHeight: number
  /** Görünür aralığın dışında çizilen satır sayısı. */
  overscan: number
}

/**
 * `@tanstack/react-virtual` sarmalayıcısı. Kütüphanenin döndürdüğü nesne kendini değiştirir (React
 * Compiler'a uygun değil); bu yüzden çağıran bileşene yalnız düz değerler (öğeler, toplam yükseklik) çıkar.
 * Satırlar sabit yüksekliktedir, ölçülmez.
 */
export function useVirtualRows({
  count,
  scrollRef,
  headHeight,
  footHeight,
  overscan,
}: UseVirtualRowsOptions) {
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan,
    scrollMargin: headHeight,
    scrollPaddingStart: headHeight,
    scrollPaddingEnd: footHeight,
    initialRect: { width: 0, height: INITIAL_HEIGHT },
  })
  const scrollToRow = useCallback(
    (row: number) => virtualizer.scrollToIndex(row, { align: 'auto' }),
    [virtualizer],
  )
  return {
    items: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
    scrollToRow,
  }
}
